
import { extractEmailDetails } from './messageUtils';

// Helper function to implement throttling for API calls
const throttlePromises = async <T>(
  promises: (() => Promise<T>)[],
  batchSize: number
): Promise<T[]> => {
  const results: T[] = [];
  
  for (let i = 0; i < promises.length; i += batchSize) {
    const batch = promises.slice(i, i + batchSize);
    const batchResults = await Promise.all(batch.map(fn => fn()));
    results.push(...batchResults);
  }
  
  return results;
};

export const fetchGmailEmails = async (token: string, pageSize: number, pageToken: string | null) => {
  try {
    const baseUrl = 'https://www.googleapis.com/gmail/v1/users/me/messages';
    const url = new URL(baseUrl);
    url.searchParams.append('maxResults', pageSize.toString());
    if (pageToken) {
      url.searchParams.append('pageToken', pageToken);
    }

    const response = await fetch(url.toString(), {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      if (response.status === 401) {
        localStorage.removeItem('gmail_access_token');
        localStorage.removeItem('gmail_token_expiry');
        throw new Error('Invalid or expired token');
      }
      throw new Error(`Failed to fetch emails: ${response.status}`);
    }

    const data = await response.json();

    if (!data.messages || !Array.isArray(data.messages)) {
      return { emails: [], nextPageToken: null };
    }

    // Prepare API call functions without executing them yet
    const fetchPromises = data.messages.map(message => async () => {
      try {
        const detailResponse = await fetch(
          `https://www.googleapis.com/gmail/v1/users/me/messages/${message.id}?format=full`,
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );
        
        if (!detailResponse.ok) {
          console.error(`Failed to fetch message ${message.id}: ${detailResponse.status}`);
          return null;
        }
        
        const messageData = await detailResponse.json();
        const emailDetails = extractEmailDetails(messageData);
        
        return {
          id: message.id,
          ...emailDetails
        };
      } catch (error) {
        console.error(`Error fetching message ${message.id}:`, error);
        return null;
      }
    });

    // Execute the promises with controlled concurrency (5 at a time)
    const batchSize = 5;
    const emails = await throttlePromises(fetchPromises, batchSize);
    
    return {
      emails: emails.filter(Boolean), // Filter out failed requests
      nextPageToken: data.nextPageToken || null
    };
  } catch (error) {
    console.error("Error in fetchEmails:", error);
    throw error;
  }
};
